import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { formatInTimeZone } from 'date-fns-tz'
import { useAuth } from '@/contexts/AuthContext'
import { isDemoMode, supabase } from '@/lib/supabase'
import * as service from '@/services/appService'
import type {
  AppProfile,
  EmployeeInput,
  Lookup,
  Meal,
  MealInput,
  Selection,
  SelectionInput,
  SystemSettings,
  PeriodRangeImpact,
  WeekBundle,
  WeekDayInput,
  WeekInput,
  WeekParticipant,
  WeekStatus,
  WeekUpdateOptions,
} from '@/types'
import { APP_TIMEZONE } from '@/utils'

export interface AppDataContextValue {
  profile: AppProfile | null
  weeks: WeekBundle[]
  activeWeek: WeekBundle | null
  weekDays: WeekBundle['days']
  employees: AppProfile[]
  meals: Meal[]
  selections: Selection[]
  weekParticipants: WeekParticipant[]
  sectors: Lookup[]
  shifts: Lookup[]
  locations: Lookup[]
  settings: SystemSettings
  loading: boolean
  saving: boolean
  refresh: () => Promise<void>
  createWeek: (input: WeekInput) => Promise<void>
  previewWeekRange: (id: string, startDate: string, endDate: string) => Promise<PeriodRangeImpact>
  updateWeek: (id: string, patch: Partial<WeekInput>, options?: WeekUpdateOptions) => Promise<void>
  deleteWeek: (id: string) => Promise<void>
  duplicateWeek: (id: string) => Promise<void>
  transitionWeek: (id: string, status: WeekStatus) => Promise<void>
  updateDay: (id: string, patch: Partial<WeekDayInput>) => Promise<void>
  assignMeal: (dayId: string, mealId: string) => Promise<void>
  unassignMeal: (dayMealId: string) => Promise<void>
  reorderDayMeals: (dayId: string, orderedDayMealIds: string[]) => Promise<void>
  createMeal: (input: MealInput) => Promise<void>
  updateMeal: (id: string, patch: Partial<MealInput>) => Promise<void>
  archiveMeal: (id: string) => Promise<void>
  uploadMealImage: (file: File) => Promise<string>
  createEmployee: (input: EmployeeInput) => Promise<void>
  updateEmployee: (id: string, patch: Partial<EmployeeInput>) => Promise<void>
  updateOwnProfile: (patch: Partial<Pick<AppProfile, 'firstName' | 'lastName' | 'email' | 'avatarUrl'>>) => Promise<void>
  toggleEmployee: (id: string) => Promise<void>
  deleteEmployee: (id: string) => Promise<void>
  resetEmployeePassword: (id: string, password: string) => Promise<void>
  saveSelection: (input: SelectionInput) => Promise<void>
  updateSelectionAsAdmin: (input: SelectionInput) => Promise<void>
  confirmSelections: (weekId: string, employeeId: string) => Promise<void>
  updateSettings: (patch: Partial<SystemSettings>) => Promise<void>
}

const EMPTY_SETTINGS: SystemSettings = {
  companyName: 'Mi empresa',
  systemName: 'Buen Plato',
  welcomeText: 'Elegí tus viandas del período de forma simple y rápida.',
  logoUrl: null,
  serviceWeekdays: [1, 2, 3, 4, 5],
  timezone: APP_TIMEZONE,
}

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined)

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { profile, refreshProfile } = useAuth()
  const [snapshot, setSnapshot] = useState<service.AppSnapshot>({
    weeks: [], meals: [], employees: [], selections: [], weekParticipants: [], sectors: [], shifts: [], locations: [], settings: EMPTY_SETTINGS,
  })
  const [loading, setLoading] = useState(Boolean(profile))
  const [saving, setSaving] = useState(false)

  const refresh = useCallback(async () => {
    if (!profile) {
      setSnapshot({ weeks: [], meals: [], employees: [], selections: [], weekParticipants: [], sectors: [], shifts: [], locations: [], settings: EMPTY_SETTINGS })
      setLoading(false)
      return
    }
    try {
      const data = await service.fetchAppSnapshot(profile)
      setSnapshot(data)
    } finally {
      setLoading(false)
    }
  }, [profile])

  useEffect(() => {
    setLoading(Boolean(profile))
    void refresh()
  }, [profile, refresh])

  useEffect(() => {
    if (!profile || isDemoMode || !supabase) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const scheduleRefresh = () => {
      clearTimeout(timer)
      timer = setTimeout(() => void refresh(), 350)
    }
    const channel = supabase
      .channel(`app-data-${profile.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'weeks' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'week_days' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'day_meals' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'selections' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'week_employees' }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, scheduleRefresh)
      .subscribe()
    return () => {
      clearTimeout(timer)
      void supabase?.removeChannel(channel)
    }
  }, [profile, refresh])

  useEffect(() => {
    const nextDeadline = snapshot.weeks
      .filter((week) => week.status === 'open')
      .map((week) => new Date(week.selectionDeadline).getTime())
      .filter((deadline) => Number.isFinite(deadline) && deadline > Date.now())
      .sort((a, b) => a - b)
      .at(0)
    if (!nextDeadline) return
    const timer = setTimeout(() => void refresh(), Math.min(2_147_000_000, nextDeadline - Date.now() + 250))
    return () => clearTimeout(timer)
  }, [refresh, snapshot.weeks])

  const mutate = useCallback(async (operation: () => Promise<void>, options?: { refreshProfile?: boolean }) => {
    setSaving(true)
    try {
      await operation()
      if (options?.refreshProfile) await refreshProfile()
      try {
        await refresh()
      } catch (error) {
        console.error('La operación se guardó, pero no se pudo recargar el estado actualizado.', error)
        setTimeout(() => void refresh().catch((retryError) => {
          console.error('Falló el reintento de actualización de datos.', retryError)
        }), 1_000)
      }
    } finally {
      setSaving(false)
    }
  }, [refresh, refreshProfile])

  const requireActor = useCallback(() => {
    if (!profile) throw new Error('Debes iniciar sesión para acceder.')
    return profile.id
  }, [profile])

  const actions = useMemo(() => ({
    createWeek: (input: WeekInput) => mutate(() => service.createWeek(input, requireActor())),
    previewWeekRange: (id: string, startDate: string, endDate: string) => service.previewWeekRange(id, startDate, endDate),
    updateWeek: (id: string, patch: Partial<WeekInput>, options?: WeekUpdateOptions) => mutate(() => service.updateWeek(id, patch, options)),
    deleteWeek: (id: string) => mutate(() => service.deleteWeek(id)),
    duplicateWeek: (id: string) => mutate(() => service.duplicateWeek(id, requireActor())),
    transitionWeek: (id: string, status: WeekStatus) => mutate(() => service.transitionWeek(id, status, requireActor())),
    updateDay: (id: string, patch: Partial<WeekDayInput>) => mutate(() => service.updateDay(id, patch)),
    assignMeal: (dayId: string, mealId: string) => mutate(() => service.assignMeal(dayId, mealId)),
    unassignMeal: (dayMealId: string) => mutate(() => service.unassignMeal(dayMealId)),
    reorderDayMeals: (dayId: string, orderedDayMealIds: string[]) => mutate(() => service.reorderDayMeals(dayId, orderedDayMealIds)),
    createMeal: (input: MealInput) => mutate(() => service.createMeal(input)),
    updateMeal: (id: string, patch: Partial<MealInput>) => mutate(() => service.updateMeal(id, patch)),
    archiveMeal: (id: string) => mutate(() => service.archiveMeal(id)),
    uploadMealImage: (file: File) => service.uploadMealImage(file, requireActor()),
    createEmployee: (input: EmployeeInput) => mutate(() => service.createEmployee(input, requireActor())),
    updateEmployee: (id: string, patch: Partial<EmployeeInput>) => mutate(() => service.updateEmployee(id, patch)),
    updateOwnProfile: (patch: Partial<Pick<AppProfile, 'firstName' | 'lastName' | 'email' | 'avatarUrl'>>) => mutate(() => service.updateOwnProfile(requireActor(), patch), { refreshProfile: true }),
    toggleEmployee: (id: string) => mutate(() => service.toggleEmployee(id)),
    deleteEmployee: (id: string) => mutate(() => service.deleteEmployee(id)),
    resetEmployeePassword: (id: string, password: string) => mutate(() => service.resetEmployeePassword(id, password)),
    saveSelection: (input: SelectionInput) => mutate(() => service.saveSelection(input)),
    updateSelectionAsAdmin: (input: SelectionInput) => mutate(() => service.saveSelection(input, true, requireActor())),
    confirmSelections: (weekId: string, employeeId: string) => mutate(() => service.confirmSelections(weekId, employeeId, profile?.role === 'admin')),
    updateSettings: (patch: Partial<SystemSettings>) => mutate(() => service.updateSettings(patch)),
  }), [mutate, profile?.role, requireActor])

  const activeWeek = useMemo(() => {
    const today = formatInTimeZone(new Date(), APP_TIMEZONE, 'yyyy-MM-dd')
    return snapshot.weeks.find((week) => week.status === 'open')
      ?? snapshot.weeks.find((week) => week.status === 'closed' && week.endDate >= today)
      ?? null
  }, [snapshot.weeks])

  const value = useMemo<AppDataContextValue>(() => ({
    profile,
    ...snapshot,
    activeWeek,
    weekDays: activeWeek?.days ?? [],
    loading,
    saving,
    refresh,
    ...actions,
  }), [actions, activeWeek, loading, profile, refresh, saving, snapshot])

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>
}

export function useAppData(): AppDataContextValue {
  const context = useContext(AppDataContext)
  if (!context) throw new Error('useAppData debe utilizarse dentro de AppDataProvider.')
  return context
}
